import { FC, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useHistory, useParams } from 'react-router-dom'
import { DateTime } from 'luxon'
import { createGlobalStyle } from 'styled-components'
import { useApolloClient } from '@apollo/client'
import {
  CoursePreviewContent,
  CoursePreviewHeading,
  CoursePreviewIncludes,
  CoursePreviewLearn
} from '../../components'
import { courseFindMany } from '../../graphql'
import { setCourse, setCourseLoading, useAppDispatch, useAppSelector } from '../../redux'
import { CourseType } from '../../types'
import { ROUTES, getOptimizedImage, useSegment } from '../../utils'
import { StyledCoursePreviewView } from '.'

const CoursePreviewView: FC = () => {
  const apollo = useApolloClient()
  const dispatch = useAppDispatch()
  const { push } = useHistory()
  const { track } = useSegment()
  const { course } = useAppSelector((state) => state.course)
  const { courseSlug }: any = useParams()

  const loadCourseBySlug = async () => {
    try {
      dispatch(setCourseLoading(true))

      const { data } = await apollo.query({
        fetchPolicy: 'no-cache',
        query: courseFindMany,
        variables: { slug: courseSlug }
      })

      const result = data?.courseFindMany?.docs[0] as CourseType

      if (!result) {
        return push(ROUTES.home)
      }

      track('Loaded Course Preview', {
        _id: result._id,
        title: result.title,
        slug: result.slug,
        cart_close: result.cartClose,
        waitlist: result.waitlist
      })

      if (result.cartClose && DateTime.fromISO(result.cartClose).diffNow('seconds').seconds < 0) {
        return window.location.assign(result.waitlist || '')
      }

      dispatch(setCourse(result))
    } catch (err) {
      console.log(err)
    } finally {
      dispatch(setCourseLoading(false))
    }
  }

  useEffect(() => {
    loadCourseBySlug()
  }, [courseSlug])

  return (
    <>
      <Helmet title={course?.title}>
        <meta name="description" content={course?.previewDescription || course?.description} />
        <link rel="canonical" href={`https://academy.socialcurator.com/${course?.slug}/preview`} />

        <meta name="og:title" content={course?.title} />
        <meta name="og:type" content="website" />
        <meta name="og:url" content={`https://academy.socialcurator.com/${course?.slug}/preview`} />
        <meta
          name="og:image"
          content={getOptimizedImage(course?.coverImage?.fileHttpLink || '', { height: 600 })}
        />
        <meta name="og:site_name" content="Social Curator" />
        <meta name="og:description" content={course?.previewDescription || course?.description} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="jasminestar" />
        <meta name="twitter:title" content={course?.title} />
        <meta
          name="twitter:image"
          content={getOptimizedImage(course?.coverImage?.fileHttpLink || '', { height: 600 })}
        />
        <meta
          name="twitter:description"
          content={course?.previewDescription || course?.description}
        />
      </Helmet>
      <StyledCoursePreviewView>
        <CoursePreviewHeading />
        <CoursePreviewLearn />
        <CoursePreviewIncludes />
        <CoursePreviewContent />
      </StyledCoursePreviewView>

      {course?._id === '752d8bce-c898-4338-b503-5e45a2942d09' && <ShowCountdownTimerGlobalCSS />}
    </>
  )
}

const ShowCountdownTimerGlobalCSS = createGlobalStyle`
  div[data-elfsight-app-lazy] {
    display: flex !important;
  }

`

export default CoursePreviewView
